/**
 * The browser's storage, or nothing.
 *
 * `window.localStorage` is a getter, and a browser told to block site data
 * — Safari's "Block all cookies", Chrome's "Block all", a company policy —
 * answers it with a SecurityError rather than with a storage that refuses.
 * `typeof localStorage` is no guard against that: it evaluates the getter
 * too. The auth store read the token while its module was evaluated, so
 * that one throw took the whole client bundle with it: the door stood as
 * the server drew it, without the Google button the client draws, the
 * diary sat on "Opening your Selfeed…" for good, and nothing said why.
 *
 * Asked through a try, and for working methods: Node ≥22 ships a global
 * localStorage that is method-less without --localstorage-file, and on the
 * SSR server "no storage" is the truth anyway.
 */
export function webStorage(kind: "local" | "session"): Storage | null {
  try {
    const storage = kind === "local" ? globalThis.localStorage : globalThis.sessionStorage;
    if (
      !storage ||
      typeof storage.getItem !== "function" ||
      typeof storage.setItem !== "function" ||
      typeof storage.removeItem !== "function"
    ) {
      return null;
    }
    return storage;
  } catch {
    return null;
  }
}

/**
 * Whether this browser will keep anything at all. The door reads it after
 * hydration: a session is a token in localStorage and nothing else, so a
 * browser that refuses the storage cannot be signed into, and the buttons
 * would only send somebody to Google and back to nothing.
 */
export function storageRefused(): boolean {
  if (typeof window === "undefined") return false;
  const storage = webStorage("local");
  if (storage === null) return true;
  // A storage that reads and will not be written is the other half of the
  // same refusal, and the commoner one: Safari's private window answered
  // every `setItem` with a quota error for years, a full disk does it
  // today, and a policy can set the quota to nothing. Nothing throws until
  // something is saved — so the door offered its buttons, the provider
  // signed the person in, the token could not be written, and the screen
  // said "Sign-in failed — try again" over a sign-in that had worked.
  // Asked once, where the door asks: a write and a removal of one key.
  try {
    storage.setItem(PROBE_KEY, "1");
    storage.removeItem(PROBE_KEY);
    return false;
  } catch {
    return true;
  }
}

const PROBE_KEY = "selfeed.web.probe";
